import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * Catches render errors so one bad component cannot take the whole application down.
 *
 * Without this, a throw during render makes React 18 unmount the entire tree. The result is
 * a blank dark page with no message, no error, and nothing to click — indistinguishable
 * from an infinite load. That is precisely how a malformed offer payload from gcstore
 * presented in production: the lock screen threw formatting a price, and the viewer saw a
 * black screen forever.
 *
 * A boundary turns that class of failure into something a person can read and act on.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Left in deliberately: this is the only trace of a crash that already destroyed the UI.
    console.error('render error', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="center" style={{ flexDirection: 'column', gap: '1rem', padding: '2rem' }}>
        <h1>Algo quebrou nesta tela</h1>
        <p className="muted">
          A página encontrou um erro e não conseguiu continuar. Recarregar costuma resolver.
        </p>
        <pre className="crash-detail">{this.state.error.message}</pre>
        <button className="btn" onClick={() => window.location.reload()}>
          Recarregar
        </button>
      </div>
    )
  }
}
